import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle2, ChevronDown, ChevronUp, Upload, FileText, X } from 'lucide-react';
import ReadyToConnectWidget from '../shared/ReadyToConnectWidget';

const categoryConfig = {
  financial: { label: 'FINANCIAL', color: 'chart-1' },
  human: { label: 'HUMAN CAPITAL', color: 'chart-2' },
  structural: { label: 'STRUCTURAL', color: 'chart-3' },
  customer: { label: 'CUSTOMER', color: 'chart-4' },
  wealth: { label: 'WEALTH PLANNING', color: 'chart-5' },
};

const categoryColors = {
  financial: 'hsl(var(--chart-1))',
  human: 'hsl(var(--chart-2))',
  structural: 'hsl(var(--chart-3))',
  customer: 'hsl(var(--chart-4))',
  wealth: 'hsl(var(--chart-5))',
};

export function SectionLabel({ text }) {
  return (
    <div className="flex items-center gap-2 mb-6">
      <div className="w-2 h-2 rounded-full bg-[hsl(var(--succeedo-orange))] shadow-[0_0_8px_hsl(var(--succeedo-orange))]" />
      <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
        {text}
      </span>
    </div>
  );
}

function StatusSelect({ status, onChange }) {
  const styles = {
    open: 'border-border text-muted-foreground',
    in_progress: 'bg-[hsl(var(--succeedo-orange))]/10 text-[hsl(var(--succeedo-orange))] border-[hsl(var(--succeedo-orange))]/20',
    completed: 'bg-[hsl(var(--chart-2))]/10 text-[hsl(var(--chart-2))] border-[hsl(var(--chart-2))]/20',
  };

  return (
    <select
      value={status}
      onChange={(e) => onChange(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      className={`px-3 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-wide border transition-colors focus:outline-none focus:ring-2 focus:ring-[hsl(var(--succeedo-orange))]/20 shrink-0 ${styles[status] || styles.open}`}
    >
      <option value="open">OPEN</option>
      <option value="in_progress">IN PROGRESS</option>
      <option value="completed">COMPLETED</option>
    </select>
  );
}

export default function ActionPlanChecklist({
  items,
  daysRemaining,
  onStatusChange,
  onRequestIntroduction,
  introductionRequests = [],
  introLoading = false,
  advisoryTeamMembers = [],
}) {
  const [expandedId, setExpandedId] = useState(null);
  const [comingSoonId, setComingSoonId] = useState(null);
  const [showCompleted, setShowCompleted] = useState(false);

  // Build a set of partner categories with active intro requests
  const activeIntroCategories = new Set(
    introductionRequests
      .filter((r) => r.status === 'pending' || r.status === 'matched')
      .map((r) => r.partner_category)
  );

  // Find the intro request for a given item (by category or action_plan_item_id)
  const getIntroRequestForItem = (item) => {
    return introductionRequests.find(
      (r) => r.action_plan_item_id === item.id ||
             (r.partner_category === item.partner_category && (r.status === 'pending' || r.status === 'matched'))
    );
  };

  // comingSoonId now shows the ReadyToConnectWidget inline — no auto-dismiss

  const activeItems = items.filter((item) => item.status !== 'completed');
  const completedItems = items.filter((item) => item.status === 'completed');
  const completedCount = completedItems.length;
  const totalCount = items.length;
  const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  const toggleExpand = useCallback((id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  }, []);

  const handleStatusChange = useCallback((itemId, newStatus) => {
    if (onStatusChange) {
      onStatusChange(itemId, newStatus);
    }
    if (newStatus === 'completed') {
      setExpandedId(null);
    }
  }, [onStatusChange]);

  const handleMarkComplete = useCallback((itemId) => {
    handleStatusChange(itemId, 'completed');
  }, [handleStatusChange]);

  const resolveCategory = (item) => item.metadata?.category || item.cepa_category || 'financial';

  return (
    <div className="mb-8" id="action-plan">
      {/* Section Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-2 h-2 rounded-full bg-[hsl(var(--succeedo-orange))] shadow-[0_0_8px_hsl(var(--succeedo-orange))]" />
          <h2 className="font-mono text-[11px] uppercase tracking-wide text-foreground">
            YOUR 90-DAY PLAN
          </h2>
        </div>
        {daysRemaining != null && (
          <div className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
            {daysRemaining} DAYS REMAINING
          </div>
        )}
      </div>

      {/* Sprint Progress Card */}
      <div className="bg-card border border-border rounded-[16px] p-5 space-y-4 mb-6">
        <div className="flex items-center justify-between text-sm">
          <div className="text-foreground font-medium">
            {completedCount} of {totalCount} complete
          </div>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--chart-2))]" />
            <div className="text-muted-foreground text-[13px]">
              All tasks on track
            </div>
          </div>
        </div>
        <div className="h-1.5 bg-border rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[hsl(var(--succeedo-orange))] to-[hsl(var(--succeedo-orange))]/70 transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Active Items */}
      <div className="space-y-3 mb-6">
        {activeItems.map((item) => {
          const isExpanded = expandedId === item.id;
          const cat = resolveCategory(item);
          const color = categoryColors[cat] || categoryColors.financial;
          const config = categoryConfig[cat] || categoryConfig.financial;
          const description = item.description || item.task_template?.description || '';
          const questionText = item.metadata?.question_text || '';

          return (
            <div key={item.id} className="bg-card border border-border rounded-[12px] overflow-hidden">
              {/* Collapsed Row */}
              <div className="p-4 flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => toggleExpand(item.id)}
                  className="flex-1 flex items-center gap-4 hover:opacity-80 transition-opacity text-left min-w-0"
                >
                  <div
                    className="px-3 py-1 rounded-full text-[11px] font-mono uppercase tracking-wide shrink-0"
                    style={{
                      border: `1px solid ${color}30`,
                      color: color,
                    }}
                  >
                    {config.label}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="text-[15px] font-medium text-foreground">{item.title}</div>
                  </div>

                  {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-muted-foreground shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-muted-foreground shrink-0" />
                  )}
                </button>

                {item.metadata?.value_impact && (
                  <span className="bg-[hsl(var(--chart-2))]/10 text-[hsl(var(--chart-2))] rounded-full px-2 py-0.5 font-mono text-[10px] shrink-0">
                    +${item.metadata.value_impact >= 1000000 ? `${(item.metadata.value_impact / 1000000).toFixed(1)}M` : `${Math.round(item.metadata.value_impact / 1000)}K`}
                  </span>
                )}

                <StatusSelect
                  status={item.status}
                  onChange={(newStatus) => handleStatusChange(item.id, newStatus)}
                />
              </div>

              {/* Expanded Details */}
              {isExpanded && (
                <div className="px-4 pb-4 space-y-5 border-t border-border pt-5">
                  {/* Description */}
                  {description && (
                    <div className="text-[13px] text-muted-foreground leading-relaxed">
                      {description}
                    </div>
                  )}

                  {/* Value Impact Callout */}
                  {item.metadata?.value_impact && (
                    <div className="bg-[hsl(var(--chart-2))]/5 border border-[hsl(var(--chart-2))]/20 rounded-[8px] px-3 py-2">
                      <div className="text-[13px] text-foreground font-medium">
                        Estimated Value Impact: +${item.metadata.value_impact >= 1000000 ? `${(item.metadata.value_impact / 1000000).toFixed(1)}M` : item.metadata.value_impact.toLocaleString()}
                      </div>
                      {item.metadata?.multiple_change && (
                        <div className="text-[12px] text-muted-foreground">
                          This improvement moves your multiple from {item.metadata.multiple_change}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Assessment Context */}
                  {questionText && (
                    <div className="bg-muted rounded-[8px] p-3 space-y-2">
                      <div className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
                        YOUR ASSESSMENT RESPONSE
                      </div>
                      <div className="text-[13px] text-foreground">{questionText}</div>
                    </div>
                  )}

                  {/* Supporting Documents */}
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground mb-3">
                      SUPPORTING DOCUMENTS
                    </div>
                    {item.documents && item.documents.length > 0 && (
                      <div className="space-y-2 mb-3">
                        {item.documents.map((doc) => (
                          <div key={doc.id} className="flex items-center gap-2 text-[13px] text-foreground">
                            <FileText className="w-4 h-4 text-muted-foreground" />
                            <a href={doc.download_url} className="hover:underline">{doc.name}</a>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="border-2 border-dashed border-border rounded-[8px] p-4 text-center hover:border-muted-foreground/50 transition-colors cursor-pointer">
                      <Upload className="w-5 h-5 text-muted-foreground mx-auto mb-2" />
                      <div className="text-[13px] text-muted-foreground">
                        Drop files or click to upload
                      </div>
                    </div>
                  </div>

                  {/* Introduction Widget */}
                  {(() => {
                    const introRequest = getIntroRequestForItem(item);
                    const hasActiveIntro = !!introRequest;
                    const widgetState = hasActiveIntro
                      ? (introRequest.status === 'accepted' || introRequest.status === 'completed' ? 'connected' : 'pending')
                      : (item.metadata?.assessment_response ? 'assessment-context' : 'generic');

                    // Conditional render decision: do we have an advisory team member matching
                    // this item's partner_category? Hooks can't be called inside .map(), so we
                    // do an array lookup here. The widget itself reads from Apollo cache via
                    // useAdvisoryTeamMember(category) — this lookup is only used to decide
                    // whether to render the widget at all.
                    const hasTeamMemberForRole = item.partner_category
                      ? advisoryTeamMembers?.some((m) => m.role === item.partner_category)
                      : false;

                    if (comingSoonId === item.id || hasActiveIntro || hasTeamMemberForRole) {
                      return (
                        <ReadyToConnectWidget
                          partnerCategory={item.partner_category}
                          state={widgetState}
                          assessmentScore={item.metadata?.assessment_score}
                          requestedTime={introRequest ? new Date(introRequest.created_at).toLocaleDateString() : null}
                          onRequestIntroduction={() => onRequestIntroduction && onRequestIntroduction(item.partner_category, item.id)}
                          loading={introLoading}
                        />
                      );
                    }
                    return null;
                  })()}

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => handleMarkComplete(item.id)}
                      className="flex-1 bg-foreground text-background rounded-full px-5 py-2 text-sm font-medium hover:opacity-90 transition-opacity inline-flex items-center justify-center gap-2"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      Mark Complete
                    </button>
                    {activeIntroCategories.has(item.partner_category) ? (
                      <button
                        type="button"
                        disabled
                        className="flex-1 border border-border text-muted-foreground/25 rounded-full px-5 py-2 text-sm cursor-not-allowed"
                      >
                        Introduction Requested &#10003;
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          if (onRequestIntroduction) {
                            setComingSoonId(item.id);
                          } else {
                            setComingSoonId(item.id);
                          }
                        }}
                        className="flex-1 border border-border text-foreground rounded-full px-5 py-2 text-sm hover:bg-accent transition-colors"
                      >
                        {item.cta_text || 'Get Expert Help'} &rarr;
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Completed Items Section */}
      {completedItems.length > 0 && (
        <div className="space-y-3">
          <button
            type="button"
            onClick={() => setShowCompleted(!showCompleted)}
            className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-wide text-muted-foreground hover:text-foreground transition-colors"
          >
            {showCompleted ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            COMPLETED ({completedItems.length})
          </button>
          {showCompleted && completedItems.map((item) => {
            const isExpanded = expandedId === item.id;
            const cat = resolveCategory(item);
            const color = categoryColors[cat] || categoryColors.financial;
            const config = categoryConfig[cat] || categoryConfig.financial;

            return (
              <div key={item.id} className="bg-card border border-border rounded-[12px] overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleExpand(item.id)}
                  className="w-full p-4 flex items-center gap-4 hover:bg-muted/30 transition-colors text-left"
                >
                  <CheckCircle2
                    className="w-5 h-5 shrink-0"
                    style={{ color: 'hsl(var(--succeedo-orange))' }}
                  />
                  <div
                    className="px-3 py-1 rounded-full text-[11px] font-mono uppercase tracking-wide shrink-0"
                    style={{
                      border: `1px solid ${color}30`,
                      color: color,
                      opacity: 0.5,
                    }}
                  >
                    {config.label}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[15px] font-medium text-foreground/35 line-through">{item.title}</div>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-muted-foreground" />
                  )}
                </button>

                {isExpanded && (
                  <div className="px-4 pb-4 space-y-4 border-t border-border pt-5 opacity-60">
                    <div className="text-[13px] text-muted-foreground leading-relaxed">
                      {item.description || item.task_template?.description || ''}
                    </div>
                    {item.metadata?.question_text && (
                      <div className="bg-muted rounded-[8px] p-3 space-y-2">
                        <div className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
                          YOUR ASSESSMENT RESPONSE
                        </div>
                        <div className="text-[13px] text-foreground">{item.metadata.question_text}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
